import React from 'react'
import { Box, Alert } from '@mui/material'
import Spinner from '../../../interaxDesignSystemComponents/Spinner'
import { useNavigate } from 'react-router-dom'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import {
  GamerFeedbackBackButton,
  TeamProfileSection,
  ExperimentalSectionCard,
  FeedbackEmptyState
} from '../../feedbackGenerator/components'
import { INTERAX_COLORS } from '../../../constants/colors'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { useSwimmerTeamDataForGamer } from '../../../apiLayer/queryHooks/queries'
import {
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  LightBulbIcon,
  TrophyIcon
} from '@heroicons/react/24/solid'
import { TEAM_PROFILE_LABELS } from '../../feedbackGenerator/utils/teamProfileMapping'
import {
  generateTeamProfileDescription,
  normalizeProfileVariable
} from '../../feedbackGenerator/utils/profileDescriptionGenerator'
import { PageWrapper } from '../../../shared/components'
import { GlowCursorProvider } from '../components/GlowCursor'
import { SparkleCursor } from '../components/SparkleCursor'
import { DEEPER_TEAM_ANALYSIS_EXPERIENCE_KEY } from '../constants/storageKeys'
import type {
  TeamSwimmerFeedbackResponse,
  TeamProfileTab,
  ImprovementAreaItem,
  StrengthItem
} from '../../../types/TeamSwimmerFeedbackTypes'
import type { SectionDataRecord } from '../../feedbackGenerator/components'

const SECTION_IDS = {
  planning: 'planning',
  psychologicalSafety: 'psychSafety',
  taskPerformance: 'taskPerformance',
  communication: 'communication',
  improvement: 'improvement',
  strengths: 'strengths'
} as const

const getCustomCursorStyles = (isActive: boolean) =>
  isActive
    ? {
        cursor: 'url(/assets/svg/mouseCursor.svg) 16 16, auto'
      }
    : {}

/**
 * DeeperTeamAnalysisPage - Data-driven deeper analysis view for team feedback
 *
 * Uses swimmer data format with array-based tab structure
 * Single source of truth: useSwimmerTeamDataForGamer
 */
export const DeeperTeamAnalysisPage: React.FC = () => {
  const [showFirstVisitExperience, setShowFirstVisitExperience] = React.useState(false)
  const [pendingSections, setPendingSections] = React.useState<string[]>([])
  const [hasInitialGlowDelayElapsed, setHasInitialGlowDelayElapsed] = React.useState(false)
  const initialPendingSectionsRef = React.useRef<string[]>([])
  const pendingInitializedRef = React.useRef(false)
  const glowDelayTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const [glintActivity, setGlintActivity] = React.useState<Record<string, boolean>>({})
  const navigate = useNavigate()
  const { t } = useTranslation()

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const hasSeenExperience = window.localStorage.getItem(DEEPER_TEAM_ANALYSIS_EXPERIENCE_KEY)
    if (!hasSeenExperience) {
      setShowFirstVisitExperience(true)
      window.localStorage.setItem(DEEPER_TEAM_ANALYSIS_EXPERIENCE_KEY, 'true')
    }
  }, [])

  // Fetch swimmer data (THE ONLY data source)
  const { data: swimmerResponse, isLoading, error } = useSwimmerTeamDataForGamer()

  const feedbackData = (swimmerResponse?.data ?? undefined) as TeamSwimmerFeedbackResponse | undefined

  const renderedSectionIds = React.useMemo(() => {
    if (!feedbackData) return []

    const planningEntries = feedbackData.planningExecutingEvaluating
    const psychologicalEntries = feedbackData.teamPsychologicalSafety
    const taskPerformanceEntries = feedbackData.taskPerformance
    const communicationEntries = feedbackData.communicationClimate
    const improvementAreas = feedbackData.improvementAreas
    const strengths = feedbackData.strengths

    const sections: string[] = []

    if (planningEntries.length >= 3) sections.push(SECTION_IDS.planning)
    if (psychologicalEntries.length >= 3) sections.push(SECTION_IDS.psychologicalSafety)
    if (taskPerformanceEntries.length >= 3) sections.push(SECTION_IDS.taskPerformance)
    if (communicationEntries.length >= 3) sections.push(SECTION_IDS.communication)
    if (improvementAreas.length > 0) sections.push(SECTION_IDS.improvement)
    if (strengths.length > 0) sections.push(SECTION_IDS.strengths)

    return sections
  }, [feedbackData])

  React.useEffect(() => {
    if (!showFirstVisitExperience) {
      if (pendingSections.length > 0) {
        setPendingSections([])
      }
      initialPendingSectionsRef.current = []
      pendingInitializedRef.current = false
      return
    }

    if (renderedSectionIds.length === 0) {
      if (pendingSections.length > 0) {
        setPendingSections([])
      }
      initialPendingSectionsRef.current = []
      pendingInitializedRef.current = false
      return
    }

    setPendingSections((prev) => {
      const filteredPrev = prev.filter((id) => renderedSectionIds.includes(id))

      if (!pendingInitializedRef.current) {
        pendingInitializedRef.current = true
        initialPendingSectionsRef.current = renderedSectionIds

        return renderedSectionIds
      }

      const knownSections = initialPendingSectionsRef.current
      const newSections = renderedSectionIds.filter((id) => !knownSections.includes(id))

      if (newSections.length > 0) {
        initialPendingSectionsRef.current = [...knownSections, ...newSections]

        return [...filteredPrev, ...newSections]
      }

      if (filteredPrev.length !== prev.length) {
        return filteredPrev
      }

      return prev
    })
  }, [showFirstVisitExperience, renderedSectionIds, pendingSections.length])

  React.useEffect(() => {
    if (showFirstVisitExperience && pendingInitializedRef.current && pendingSections.length === 0) {
      pendingInitializedRef.current = false
      setShowFirstVisitExperience(false)
    }
  }, [showFirstVisitExperience, pendingSections.length])

  React.useEffect(() => {
    if (!showFirstVisitExperience) {
      setHasInitialGlowDelayElapsed(false)
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
      return
    }

    if (isLoading || error || !feedbackData || pendingSections.length === 0) {
      setHasInitialGlowDelayElapsed(false)
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
      return
    }

    if (hasInitialGlowDelayElapsed || glowDelayTimeoutRef.current) return

    glowDelayTimeoutRef.current = setTimeout(() => {
      setHasInitialGlowDelayElapsed(true)
      glowDelayTimeoutRef.current = null
    }, 300)

    return () => {
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
    }
  }, [showFirstVisitExperience, isLoading, error, feedbackData, pendingSections.length, hasInitialGlowDelayElapsed])

  React.useEffect(
    () => () => {
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
    },
    []
  )

  const isGlowActive =
    showFirstVisitExperience &&
    pendingSections.length > 0 &&
    !isLoading &&
    !error &&
    Boolean(feedbackData) &&
    hasInitialGlowDelayElapsed

  const isGlintActive = React.useMemo(() => Object.values(glintActivity).some(Boolean), [glintActivity])

  const handleSectionGlintStateChange = React.useCallback((sectionId: string, state: 'idle' | 'running') => {
    setGlintActivity((prev) => {
      const nextState = state === 'running'
      const currentState = prev[sectionId] ?? false
      if (currentState === nextState) {
        return prev
      }
      if (!nextState) {
        const { [sectionId]: _, ...rest } = prev
        return rest
      }
      return {
        ...prev,
        [sectionId]: true
      }
    })
  }, [])

  const handleSectionReveal = React.useCallback((sectionId: string) => {
    setPendingSections((prev) => {
      if (!prev.includes(sectionId)) return prev
      const next = prev.filter((id) => id !== sectionId)

      return next
    })
  }, [])

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: INTERAX_COLORS.offBlack,
          ...getCustomCursorStyles(false)
        }}
      >
        <Spinner size="large" color="sun" />
      </PageWrapper>
    )
  }

  // Error state
  if (error) {
    return (
      <PageWrapper sx={{ backgroundColor: INTERAX_COLORS.offBlack, ...getCustomCursorStyles(false) }}>
        <Alert severity="error">Failed to load team feedback data</Alert>
      </PageWrapper>
    )
  }

  // Extract swimmer data (data IS the teamSessionFeedbackSwimmer)
  if (!feedbackData) {
    return (
      <PageWrapper sx={{ backgroundColor: INTERAX_COLORS.offBlack, ...getCustomCursorStyles(false) }}>
        <GamerFeedbackBackButton
          label={t('gamer.feedback.deeperAnalysis.backToFeedback')}
          onClick={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}
        />
        <Box
          sx={{
            marginTop: '24px',
            background: `linear-gradient(148deg, ${INTERAX_COLORS.heritageBlue40} 0%, ${INTERAX_COLORS.white08} 100%)`,
            border: `1px solid ${INTERAX_COLORS.white04}`,
            boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white08}`,
            borderRadius: '10px',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FeedbackEmptyState />
        </Box>
      </PageWrapper>
    )
  }

  const planningEntries = feedbackData.planningExecutingEvaluating
  const psychologicalEntries = feedbackData.teamPsychologicalSafety
  const taskPerformanceEntries = feedbackData.taskPerformance
  const communicationEntries = feedbackData.communicationClimate
  const improvementAreas = feedbackData.improvementAreas
  const strengths = feedbackData.strengths

  // Get team profile from array (first element)
  const teamProfile: TeamProfileTab | undefined = feedbackData.teamSessionProfile[0]

  if (!teamProfile) {
    return (
      <PageWrapper sx={{ backgroundColor: INTERAX_COLORS.offBlack, ...getCustomCursorStyles(false) }}>
        <GamerFeedbackBackButton
          label={t('gamer.feedback.deeperAnalysis.backToFeedback')}
          onClick={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}
        />
        <Box
          sx={{
            marginTop: '24px',
            background: `linear-gradient(148deg, ${INTERAX_COLORS.heritageBlue40} 0%, ${INTERAX_COLORS.white08} 100%)`,
            border: `1px solid ${INTERAX_COLORS.white04}`,
            boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white08}`,
            borderRadius: '10px',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FeedbackEmptyState />
        </Box>
      </PageWrapper>
    )
  }

  // Generate team profile name from topStrength + secondStrength
  const normalizedTopVariable = normalizeProfileVariable(teamProfile.topStrength.variable)
  const normalizedSecondVariable = normalizeProfileVariable(teamProfile.secondStrength.variable)

  const topStrengthLabel =
    TEAM_PROFILE_LABELS[normalizedTopVariable] ?? TEAM_PROFILE_LABELS[teamProfile.topStrength.variable]
  const secondStrengthLabel =
    TEAM_PROFILE_LABELS[normalizedSecondVariable] ?? TEAM_PROFILE_LABELS[teamProfile.secondStrength.variable]
  const teamProfileName =
    topStrengthLabel && secondStrengthLabel
      ? `${topStrengthLabel.topStrength} ${secondStrengthLabel.secondStrength}`
      : 'Your Team'

  // Transform array data to object format for ExperimentalSectionCard
  const planningData: SectionDataRecord = {}
  if (renderedSectionIds.includes(SECTION_IDS.planning) && planningEntries.length >= 3) {
    const [planEntry, followEntry, evaluateEntry] = planningEntries
    if (planEntry) planningData.plan = planEntry
    if (followEntry) planningData.follow = followEntry
    if (evaluateEntry) planningData.evaluate = evaluateEntry
  }

  const psychSafetyData: SectionDataRecord = {}
  if (renderedSectionIds.includes(SECTION_IDS.psychologicalSafety) && psychologicalEntries.length >= 3) {
    const [psychologicalSafetyEntry, teamHealthEntry, constructiveDisagreementEntry] = psychologicalEntries
    if (psychologicalSafetyEntry) psychSafetyData.psychologicalSafety = psychologicalSafetyEntry
    if (teamHealthEntry) psychSafetyData.teamHealth = teamHealthEntry
    if (constructiveDisagreementEntry) {
      psychSafetyData.constructiveDisagreement = constructiveDisagreementEntry
    }
  }

  const taskPerformanceData: SectionDataRecord = {}
  if (renderedSectionIds.includes(SECTION_IDS.taskPerformance) && taskPerformanceEntries.length >= 3) {
    const [performanceEntry, hypothermiaEntry, droneUsageEntry] = taskPerformanceEntries
    if (performanceEntry) taskPerformanceData.performance = performanceEntry
    if (hypothermiaEntry) taskPerformanceData.hypothermia = hypothermiaEntry
    if (droneUsageEntry) taskPerformanceData.droneUsage = droneUsageEntry
  }

  const communicationData: SectionDataRecord = {}
  if (renderedSectionIds.includes(SECTION_IDS.communication) && communicationEntries.length >= 3) {
    const [balanceEntry, emotionsEntry, inclusionEntry] = communicationEntries
    if (balanceEntry) communicationData.communicationBalance = balanceEntry
    if (emotionsEntry) communicationData.communicatedEmotions = emotionsEntry
    if (inclusionEntry) communicationData.inclusionAndInvitations = inclusionEntry
  }

  const improvementAreasTabs = improvementAreas.map((item: ImprovementAreaItem, index: number) => ({
    label: t(`team.weaknesses.${item.variable}.label`, {
      ns: 'feedbackTexts',
      defaultValue: item.variable
    }),
    key: `improvement_${index}`
  }))
  const improvementAreasData = improvementAreas.reduce<SectionDataRecord>((acc, item, index) => {
    acc[`improvement_${index}`] = item
    return acc
  }, {})

  const strengthsTabs = strengths.map((item: StrengthItem, index: number) => ({
    label: t(`team.strengths.${item.variable}.label`, {
      ns: 'feedbackTexts',
      defaultValue: item.variable
    }),
    key: `strength_${index}`
  }))
  const strengthsData = strengths.reduce<SectionDataRecord>((acc, item, index) => {
    acc[`strength_${index}`] = item
    return acc
  }, {})

  const cursorStyles = getCustomCursorStyles(isGlowActive)

  return (
    <GlowCursorProvider active={isGlowActive} showGlobalGlow>
      <SparkleCursor active={isGlowActive && isGlintActive} />
      <PageWrapper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          backgroundColor: INTERAX_COLORS.offBlack,
          position: 'relative',
          ...cursorStyles
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 20, display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Back Button */}
          <GamerFeedbackBackButton
            label={t('gamer.feedback.deeperAnalysis.backToFeedback')}
            onClick={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}
          />

          {/* Team Profile Section - using TeamProfileSection component */}
          <TeamProfileSection
            profileName={teamProfileName}
            description={generateTeamProfileDescription(teamProfile.topStrength, teamProfile.secondStrength, t)}
            traits={[
              {
                name: t(`team.profile.${normalizedTopVariable}.label`, {
                  ns: 'feedbackTexts',
                  defaultValue: teamProfile.topStrength.variable
                }),
                level: 'excellent',
                levelLabel: 'Excellent'
              },
              {
                name: t(`team.profile.${normalizedSecondVariable}.label`, {
                  ns: 'feedbackTexts',
                  defaultValue: teamProfile.secondStrength.variable
                }),
                level: 'good',
                levelLabel: 'Good'
              }
            ]}
            variant="team"
            headerTitle="Team Profile"
            headerIcon={<UserGroupIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
          />

          {/* Sections Grid - 2 columns on screens 1500px+ */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', xl: 'repeat(2, 1fr)' },
              gap: '24px',
              width: '100%'
            }}
          >
            {/* Planning Executing Evaluating Section - 3 tabs */}
            <ExperimentalSectionCard
              titleKey="gamer.feedback.sections.planningExecutingEvaluating.title"
              descriptionKey="gamer.feedback.sections.planningExecutingEvaluating.description"
              tabs={[
                { label: t('gamer.feedback.sections.planningExecutingEvaluating.tabs.plan'), key: 'plan' },
                { label: t('gamer.feedback.sections.planningExecutingEvaluating.tabs.follow'), key: 'follow' },
                { label: t('gamer.feedback.sections.planningExecutingEvaluating.tabs.evaluate'), key: 'evaluate' }
              ]}
              sectionData={planningData}
              variant="team"
              icon={<ClipboardDocumentListIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
              showGlowExperience={pendingSections.includes(SECTION_IDS.planning)}
              onGlowExperienceComplete={() => handleSectionReveal(SECTION_IDS.planning)}
              onGlintStateChange={(state) => handleSectionGlintStateChange(SECTION_IDS.planning, state)}
            />

            {/* Team Psychological Safety Section - 3 tabs */}
            <ExperimentalSectionCard
              titleKey="gamer.feedback.sections.teamPsychologicalSafety.title"
              descriptionKey="gamer.feedback.sections.teamPsychologicalSafety.description"
              tabs={[
                {
                  label: t('gamer.feedback.sections.teamPsychologicalSafety.tabs.safetyQuestions'),
                  key: 'psychologicalSafety'
                },
                { label: t('gamer.feedback.sections.teamPsychologicalSafety.tabs.teamHealth'), key: 'teamHealth' },
                {
                  label: t('gamer.feedback.sections.teamPsychologicalSafety.tabs.constructiveDebate'),
                  key: 'constructiveDisagreement'
                }
              ]}
              sectionData={psychSafetyData}
              variant="team"
              icon={<ShieldCheckIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
              showGlowExperience={pendingSections.includes(SECTION_IDS.psychologicalSafety)}
              onGlowExperienceComplete={() => handleSectionReveal(SECTION_IDS.psychologicalSafety)}
              onGlintStateChange={(state) => handleSectionGlintStateChange(SECTION_IDS.psychologicalSafety, state)}
            />

            {/* Task Performance Section - 3 tabs */}
            <ExperimentalSectionCard
              titleKey="gamer.feedback.sections.taskPerformance.title"
              descriptionKey="gamer.feedback.sections.taskPerformance.description"
              tabs={[
                { label: t('gamer.feedback.sections.taskPerformance.tabs.performance'), key: 'performance' },
                { label: t('gamer.feedback.sections.taskPerformance.tabs.hypothermia'), key: 'hypothermia' },
                { label: t('gamer.feedback.sections.taskPerformance.tabs.droneUsage'), key: 'droneUsage' }
              ]}
              sectionData={taskPerformanceData}
              variant="team"
              icon={<ChartBarIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
              showGlowExperience={pendingSections.includes(SECTION_IDS.taskPerformance)}
              onGlowExperienceComplete={() => handleSectionReveal(SECTION_IDS.taskPerformance)}
              onGlintStateChange={(state) => handleSectionGlintStateChange(SECTION_IDS.taskPerformance, state)}
            />

            {/* Communication Climate Section - 3 tabs */}
            <ExperimentalSectionCard
              titleKey="gamer.feedback.sections.communicationClimate.title"
              descriptionKey="gamer.feedback.sections.communicationClimate.description"
              tabs={[
                { label: t('gamer.feedback.sections.communicationClimate.tabs.balance'), key: 'communicationBalance' },
                { label: t('gamer.feedback.sections.communicationClimate.tabs.emotions'), key: 'communicatedEmotions' },
                {
                  label: t('gamer.feedback.sections.communicationClimate.tabs.inclusion'),
                  key: 'inclusionAndInvitations'
                }
              ]}
              sectionData={communicationData}
              variant="team"
              icon={<ChatBubbleLeftRightIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
              showGlowExperience={pendingSections.includes(SECTION_IDS.communication)}
              onGlowExperienceComplete={() => handleSectionReveal(SECTION_IDS.communication)}
              onGlintStateChange={(state) => handleSectionGlintStateChange(SECTION_IDS.communication, state)}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', xl: 'repeat(2, 1fr)' },
              gap: '24px',
              width: '100%'
            }}
          >
            {/* Improvement Areas Section - Using ExperimentalSectionCard */}
            {feedbackData.improvementAreas.length > 0 && (
              <ExperimentalSectionCard
                titleKey="gamer.feedback.sections.improvementAreas.title"
                descriptionKey="gamer.feedback.sections.improvementAreas.description"
                tabs={improvementAreasTabs}
                sectionData={improvementAreasData}
                variant="team"
                icon={<LightBulbIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
                showGlowExperience={pendingSections.includes(SECTION_IDS.improvement)}
                onGlowExperienceComplete={() => handleSectionReveal(SECTION_IDS.improvement)}
                onGlintStateChange={(state) => handleSectionGlintStateChange(SECTION_IDS.improvement, state)}
              />
            )}

            {/* Strengths Section - Using ExperimentalSectionCard */}
            {feedbackData.strengths.length > 0 && (
              <ExperimentalSectionCard
                titleKey="gamer.feedback.sections.strengths.title"
                descriptionKey="gamer.feedback.sections.strengths.description"
                tabs={strengthsTabs}
                sectionData={strengthsData}
                variant="team"
                icon={<TrophyIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
                showGlowExperience={pendingSections.includes(SECTION_IDS.strengths)}
                onGlowExperienceComplete={() => handleSectionReveal(SECTION_IDS.strengths)}
                onGlintStateChange={(state) => handleSectionGlintStateChange(SECTION_IDS.strengths, state)}
              />
            )}
          </Box>
        </Box>
      </PageWrapper>
    </GlowCursorProvider>
  )
}

export default DeeperTeamAnalysisPage
